import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api';

export const periodKeys = {
  all: ['periods'],
  list: (filters = {}) => ['periods', 'list', filters],
  fiscalYears: ['periods', 'fiscal-years'],
};

export const usePeriods = (filters = {}, options = {}) =>
  useQuery({
    queryKey: periodKeys.list(filters),
    queryFn: async () => get('/periods', { params: { limit: 100, ...filters } }),
    staleTime: 5 * 60_000,
    ...options,
  });

export const useFiscalYears = (options = {}) =>
  useQuery({
    queryKey: periodKeys.fiscalYears,
    queryFn: async () => (await get('/periods/fiscal-years')).data,
    staleTime: 10 * 60_000,
    ...options,
  });

/**
 * **Open a year's calendar** (docs/10 §26).
 *
 * The seed creates the fiscal year covering *today* and nothing else, so a document dated
 * in any other year falls in no period at all and `resolvePeriod` refuses it — correctly,
 * under **I6**, with `PERIOD_MISSING`. Nothing in the interface could then create that
 * year, so backdating an expense to last December was simply impossible: an accurate error
 * about a state the operator had no way to change.
 *
 * This is not the lock/close/reopen workflow the roadmap defers to Phase 13, and the
 * distinction is the point. Reopening a *closed* period means signed-off statements are
 * about to move, which needs a reason and an audit trail. Creating a year that has never
 * existed changes no figure anywhere — it adds twelve empty open months.
 */
export const useCreateFiscalYear = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ year }) => {
      /* Calendar year. `FISCAL_YEAR_START_MONTH` is a server setting and the server
         re-derives the periods from these dates anyway, so sending January–December is a
         request for "that year", not an assertion about where the year starts. */
      const startDate = new Date(Date.UTC(year, 0, 1)).toISOString();
      const endDate = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999)).toISOString();

      return (await post('/periods/fiscal-years', { year, startDate, endDate, generatePeriods: true })).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: periodKeys.all }),
  });
};

/**
 * The period covering today — the topbar badge (docs/04 §1).
 *
 * There is no `/periods/current` endpoint, and adding one for a badge would be a server
 * change in a client phase. The calendar is at most twelve rows per year, so the list
 * comes down once and the containing period is picked here.
 *
 * A caller without `period.view` gets a 403; the query is simply disabled for them and
 * the badge hides rather than showing an error in the chrome.
 */
export const useCurrentPeriod = ({ enabled = true } = {}) => {
  const query = usePeriods({ limit: 100 }, { enabled });

  const now = Date.now();
  const current = (query.data?.data || []).find(
    (period) => new Date(period.startDate) <= now && new Date(period.endDate) >= now,
  );

  return { ...query, period: current ?? null };
};

export default usePeriods;
